<script setup lang="ts">
  import { computed, inject, ref, watch } from 'vue'
  import { Button, Icon, Keyboard, toast } from '@rapoo/element'
  import type {
    KeyboardConfig,
    KeyboardKeyEventPayload,
    KeyConfig,
    KeyEventName,
  } from '@rapoo/element'
  import type { TglSlotInfo } from '@rapoo/device-sdk'
  import { useLocaleStore } from '@/stores'
  import { getDragData, setDragData, DragType } from '@/utils/dnd'
  import { fullSize108 } from '@/utils/fullSize108'
  import { useDragImage } from '../../../composables/useDragImage'
  import {
    KEYCODE_KEYWORDS_MAP,
    buildKeySettingFromPayload,
    getLocalizedField,
  } from '../../../utils/keyMapSettings'
  import {
    GCOME_MATRIX_TYPE,
    gcomeTripleToKeyConfig,
    keyConfigToGcomeTriple,
  } from '../../../utils/advancedKeyMapSettings'
  import {
    KeyLayerSymbol,
    KeySettingsSymbol,
    SelectedKeySymbol,
    type KeyChangePayload,
  } from '../../../types'
  import { findSettingByTargetKey, parseNumber } from '../../../utils/keyIdentity'
  import { useAdvancedKeyCommit } from '../composables/useAdvancedKeyCommit'

  const emit = defineEmits<{
    success: []
    cancel: []
  }>()

  const { t } = useLocaleStore()
  const { commitAdvancedKey } = useAdvancedKeyCommit()
  const { setDragImage } = useDragImage()

  const selectedKey = inject(SelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const currentLayer = inject(KeyLayerSymbol)

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart']

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row.map((key) => {
          const { w, h, x, y, keywords, ...rest } = key
          const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

          return {
            ...rest,
            ...config,
            rect: { w, h, x, y },
          }
        }),
      ),
    }
  })

  const APPLY_TOAST_ID = 'advanced-tgl-apply'

  const currentSelection = ref<KeyConfig | null>(null)
  const isDraggingOver = ref(false)
  const isSubmitting = ref(false)

  const currentSelectionLabel = computed(() => {
    if (!currentSelection.value) return ''
    return getLocalizedField(currentSelection.value.alias || currentSelection.value.name)
  })

  const isCanEnabled = computed(() =>
    Boolean(selectedKey?.value && currentSelection.value && !isSubmitting.value),
  )

  const restoreSelectionFromTgl = (tgl: TglSlotInfo) => {
    currentSelection.value = gcomeTripleToKeyConfig(tgl)
  }

  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key || !keySettings?.value) return

      const setting = findSettingByTargetKey(keySettings.value, key)
      if (setting?.keyType !== 'TGL') return
      if (!setting.raw?.tgl) return

      restoreSelectionFromTgl(setting.raw.tgl as TglSlotInfo)
    },
    { immediate: true },
  )

  watch(isCanEnabled, (enabled, wasEnabled) => {
    if (enabled && !wasEnabled) {
      toast.info(t('toast.keys.tglApply'), { id: APPLY_TOAST_ID })
    }
  })

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    isDraggingOver.value = true
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDragLeave = () => {
    isDraggingOver.value = false
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    isDraggingOver.value = false

    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return

    const draggedData = getDragData(dataTransfer, DragType.KEY_MAPPING)
    if (draggedData) {
      currentSelection.value = draggedData as KeyConfig
    }
  }

  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      currentSelection.value = data
      return
    }

    if (eventName === 'dragstart') {
      const event = nativeEvent as DragEvent
      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      setDragData(dataTransfer, DragType.KEY_MAPPING, data as any)
      setDragImage(event, {
        text: getLocalizedField(data.alias || data.name),
      })
    }
  }

  const resolveTargetIndex = () => {
    if (!selectedKey?.value || !keySettings?.value) return undefined
    const existingSetting = findSettingByTargetKey(keySettings.value, selectedKey.value)
    return parseNumber(existingSetting?.raw?.index) ?? parseNumber(selectedKey.value.key)
  }

  const handleTakeEffect = async () => {
    if (!selectedKey?.value) {
      return
    }

    if (!currentSelection.value) {
      return
    }

    const targetIndex = resolveTargetIndex()
    if (targetIndex == null) return

    const existingSetting = keySettings?.value
      ? findSettingByTargetKey(keySettings.value, selectedKey.value)
      : undefined

    const triple = keyConfigToGcomeTriple(currentSelection.value)
    const tgl: TglSlotInfo = {
      index: 0,
      type: triple.type,
      codeL: triple.codeL,
      codeH: triple.codeH,
    }

    const payload: KeyChangePayload = {
      keywords: selectedKey.value.keywords,
      keyType: 'TGL',
      target: null,
      raw: {
        type: GCOME_MATRIX_TYPE.TGL,
        tgl,
        clickKeyLabel: getLocalizedField(
          currentSelection.value.alias || currentSelection.value.name,
        ),
      },
    }

    const newSetting = buildKeySettingFromPayload({
      sourceData: payload,
      index: targetIndex,
    })
    if (!newSetting) return

    if (existingSetting?.raw?.tglIndex != null) {
      newSetting.raw.tglIndex = existingSetting.raw.tglIndex
    }

    let didSucceed = false
    isSubmitting.value = true
    toast.dismiss(APPLY_TOAST_ID)
    try {
      const committed = await commitAdvancedKey({
        setting: newSetting,
        existingSetting,
        isFn: currentLayer?.value === 'fn',
      })
      if (!committed) return

      toast.dismiss('keyboard')
      emit('success')
      didSucceed = true
    } finally {
      if (!didSucceed) isSubmitting.value = false
    }
  }
</script>

<template>
  <div class="w-full flex justify-center gap-2">
    <div class="flex flex-col glass-frosted rounded-8xl w-83 h-67.5 p-4">
      <div class="flex justify-between">
        <Button color="normal" class="w-12 h-6" size="sm" @click="emit('cancel')">
          {{ $t('common.backButton') }}
        </Button>
        <Button
          color="glass"
          class="w-12 h-6"
          size="sm"
          :active="isCanEnabled"
          :disabled="!isCanEnabled"
          @click="handleTakeEffect"
        >
          {{ $t('keys.tgl.apply') }}
        </Button>
      </div>

      <div class="flex-1 flex justify-center items-center gap-8 mt-2">
        <div
          class="flex flex-col items-center text-text-important-default"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
          @drop="handleDrop"
        >
          <div
            :class="`relative flex justify-center items-center w-14 h-14 px-0.5 text-black-white rounded-xl border border-solid bg-key cursor-pointer transition-all border-brand-blue1`"
          >
            <span class="w-full text-center text-l-medium leading-4 line-clamp-2">
              {{ currentSelectionLabel }}
            </span>
            <Icon
              v-show="currentSelection"
              icon="Function-Fill-Close"
              class="absolute -top-1.25 -right-1.25 w-4 h-4 text-brand-red cursor-pointer"
              @click.stop="currentSelection = null"
            />
          </div>
          <span class="text-m-medium text-black-white">{{ $t('keys.tgl.sustainedTrigger') }}</span>
        </div>
      </div>

      <div class="mt-2 text-text-glass-default text-s-medium">
        <span>{{ $t('keys.tgl.description') }}</span>
        <div class="flex mt-2">
          <Icon icon="Function-FiII-Description" class="w-4 h-4 mt-0.5 mr-0.5" />
          <span>{{ $t('keys.tgl.guide') }}</span>
        </div>
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          key-class="flex items-center justify-center gap-1 bg-key text-black-white font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer hover:text-text-glass-default active:bg-white active:text-brand-blue2 disabled:text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none"
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
